// File: src/components/SlotTemplates.jsx
// Modified for use inside Templates.jsx wrapper
// Buttons moved to preview-panel for better layout

import React, { useState, useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { 
  Calendar, Clock, ChevronLeft, ChevronRight, Settings, Save, 
  Plus, Trash2, Coffee, UserX, Download, List 
} from 'lucide-react';
import { bookingTemplateAPI } from '../services/api';
import { addMonths, getTodayDate, getMondayOfCurrentWeek, formatDateGB } from '../utils/DateUtils';
import { AUTH0_NAMESPACE, DAYS_OF_WEEK, SLOT_DURATIONS, BREAK_DURATIONS, DEFAULT_SETTINGS } from '../utils/constants';
import '../styles/modules.css';
import '../styles/global.css';
import '../styles/components.css';

export default function SlotTemplates() {
  const { user, getAccessTokenSilently } = useAuth0();
  
  const userRoles = user?.[`${AUTH0_NAMESPACE}/roles`] || [];
  const isServiceProvider = userRoles.includes('ServiceProvider');

  const today = getTodayDate();
  const futureDate = addMonths(new Date(), DEFAULT_SETTINGS.DATE_RANGE_MONTHS);

  const [settings, setSettings] = useState({
    slotName: `Myslot-${formatDateGB(new Date())}-${user?.name || 'ServiceProvider'}`,
    dateFrom: today,
    dateTo: futureDate,
    dayFrom: DEFAULT_SETTINGS.DEFAULT_DAY_FROM,
    dayTo: DEFAULT_SETTINGS.DEFAULT_DAY_TO,
    startTime: DEFAULT_SETTINGS.DEFAULT_START_TIME,
    endTime: DEFAULT_SETTINGS.DEFAULT_END_TIME,
    slotDuration: DEFAULT_SETTINGS.DEFAULT_SLOT_DURATION,
    breakDuration: DEFAULT_SETTINGS.DEFAULT_BREAK_DURATION,
    daysInAdvance: DEFAULT_SETTINGS.DEFAULT_DAYS_IN_ADVANCE,
    myBreakFrom: '',
    myBreakTo: ''
  });

  const [privateAppointments, setPrivateAppointments] = useState([]);
  const [currentWeekStart, setCurrentWeekStart] = useState(getMondayOfCurrentWeek());
  const [hasChanges, setHasChanges] = useState(false);
  const [savedTemplates, setSavedTemplates] = useState([]);
  const [currentTemplateId, setCurrentTemplateId] = useState(null);
  const [currentTemplateIsActive, setCurrentTemplateIsActive] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showTemplateList, setShowTemplateList] = useState(false);
  const [saveMessage, setSaveMessage] = useState(null);

  useEffect(() => {
    if (isServiceProvider) {
      loadUserTemplates();
    }
  }, [isServiceProvider]);

  const loadUserTemplates = async () => {
    setIsLoading(true);
    try {
      const response = await bookingTemplateAPI.getUserTemplates(getAccessTokenSilently);
      if (response.success) {
        setSavedTemplates(response.data);
        
        // Auto-load active template if exists and no template is currently loaded
        if (!currentTemplateId && response.data.length > 0) {
          const activeTemplate = response.data.find(t => t.isActive);
          if (activeTemplate) {
            loadTemplate(activeTemplate);
            showMessage(`Active template loaded: ${activeTemplate.templateName}`, 'success');
          }
        }
      }
    } catch (error) {
      console.error('Failed to load templates:', error);
      showMessage('Failed to load templates', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const saveTemplate = async () => {
    if (!isServiceProvider) {
      showMessage('Only ServiceProvider/Instructors can save templates', 'error');
      return;
    }

    setIsLoading(true);
    try {
      const templateData = { settings, privateAppointments };
      let response;

      if (currentTemplateId) {
        response = await bookingTemplateAPI.updateTemplate(currentTemplateId, templateData, getAccessTokenSilently);
        showMessage('Template updated successfully!', 'success');
      } else {
        response = await bookingTemplateAPI.saveTemplate(templateData, getAccessTokenSilently);
        setCurrentTemplateId(response.data.templateId);
        setCurrentTemplateIsActive(response.data.isActive);
        showMessage(`Template saved successfully as ${response.data.isActive ? 'ACTIVE' : 'INACTIVE'}!`, 'success');
      }

      setHasChanges(false);
      await loadUserTemplates();
    } catch (error) {
      console.error('Failed to save template:', error);
      showMessage(error.message || 'Failed to save template', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const loadTemplate = (template) => {
    setSettings(template.settings);
    setPrivateAppointments(template.privateAppointments || []);
    setCurrentTemplateId(template.templateId);
    setCurrentTemplateIsActive(template.isActive);
    setHasChanges(false);
    setShowTemplateList(false);
    showMessage(`Editing: ${template.templateName}`, 'success');
  };

  const deleteTemplate = async (templateId, templateName) => {
    if (!window.confirm(`Delete "${templateName}"?`)) return;

    setIsLoading(true);
    try {
      await bookingTemplateAPI.deleteTemplate(templateId, getAccessTokenSilently);
      if (templateId === currentTemplateId) {
        resetToNewTemplate();
      }
      await loadUserTemplates();
      showMessage('Template deleted successfully', 'success');
    } catch (error) {
      showMessage(error.message || 'Failed to delete template', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const resetToNewTemplate = () => {
    if (savedTemplates.length >= 2) {
      showMessage('Maximum 2 templates allowed', 'error');
      return;
    }

    const today = getTodayDate();
    const futureDate = addMonths(new Date(), DEFAULT_SETTINGS.DATE_RANGE_MONTHS);
    
    setSettings({
      slotName: `Myslot-${formatDateGB(new Date())}-${user?.name || 'ServiceProvider'}`,
      dateFrom: today,
      dateTo: futureDate,
      dayFrom: DEFAULT_SETTINGS.DEFAULT_DAY_FROM,
      dayTo: DEFAULT_SETTINGS.DEFAULT_DAY_TO,
      startTime: DEFAULT_SETTINGS.DEFAULT_START_TIME,
      endTime: DEFAULT_SETTINGS.DEFAULT_END_TIME,
      slotDuration: DEFAULT_SETTINGS.DEFAULT_SLOT_DURATION,
      breakDuration: DEFAULT_SETTINGS.DEFAULT_BREAK_DURATION,
      daysInAdvance: DEFAULT_SETTINGS.DEFAULT_DAYS_IN_ADVANCE,
      myBreakFrom: '',
      myBreakTo: ''
    });
    setPrivateAppointments([]);
    setCurrentTemplateId(null);
    setCurrentTemplateIsActive(null);
    setHasChanges(false);
    showMessage('New template created', 'success');
  };

  const showMessage = (message, type) => {
    setSaveMessage({ message, type });
    setTimeout(() => setSaveMessage(null), 3000);
  };

  const handleSettingChange = (field, value) => {
    setSettings(prev => ({ ...prev, [field]: value }));
    setHasChanges(true);
  };

  const navigateWeek = (direction) => {
    const newDate = new Date(currentWeekStart);
    newDate.setDate(currentWeekStart.getDate() + (direction * 7));
    setCurrentWeekStart(newDate);
  };

  const addPrivateAppointment = () => {
    setPrivateAppointments([...privateAppointments, {
      id: Date.now(),
      date: new Date().toISOString().split('T')[0],
      timeFrom: '12:00',
      timeTo: '13:00'
    }]);
    setHasChanges(true);
  };

  const updatePrivateAppointment = (id, field, value) => {
    setPrivateAppointments(prev =>
      prev.map(apt => apt.id === id ? { ...apt, [field]: value } : apt)
    );
    setHasChanges(true);
  };

  const deletePrivateAppointment = (id) => {
    setPrivateAppointments(prev => prev.filter(apt => apt.id !== id));
    setHasChanges(true);
  };

  const getWeekDates = (startDate) => {
    const dates = [];
    const start = new Date(startDate);
    for (let i = 0; i < 7; i++) {
      const date = new Date(start);
      date.setDate(start.getDate() + i);
      dates.push(date);
    }
    return dates;
  };

  const isDateInRange = (date) => {
    const checkDate = new Date(date);
    const from = new Date(settings.dateFrom);
    const to = new Date(settings.dateTo);
    return checkDate >= from && checkDate <= to;
  };

  const isDayEnabled = (date) => {
    const dayName = DAYS_OF_WEEK[date.getDay()];
    const fromIndex = DAYS_OF_WEEK.indexOf(settings.dayFrom);
    const toIndex = DAYS_OF_WEEK.indexOf(settings.dayTo);
    const dayIndex = DAYS_OF_WEEK.indexOf(dayName);
    
    if (fromIndex <= toIndex) {
      return dayIndex >= fromIndex && dayIndex <= toIndex;
    } else {
      return dayIndex >= fromIndex || dayIndex <= toIndex;
    }
  };

  const isDateBookable = (date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const checkDate = new Date(date);
    checkDate.setHours(0, 0, 0, 0);
    const advanceDate = new Date(today);
    advanceDate.setDate(today.getDate() + settings.daysInAdvance);
    return checkDate >= today && checkDate <= advanceDate;
  };

  const timeToMinutes = (timeStr) => {
    const [hours, minutes] = timeStr.split(':').map(Number);
    return hours * 60 + minutes;
  };

  const isSlotBlocked = (date, slotStart, slotEnd) => {
    const slotStartMin = timeToMinutes(slotStart);
    const slotEndMin = timeToMinutes(slotEnd);
    
    // Check private appointments only (daily break is handled as separate row)
    const dateStr = date.toISOString().split('T')[0];
    for (const apt of privateAppointments) {
      if (apt.date === dateStr) {
        const aptStartMin = timeToMinutes(apt.timeFrom);
        const aptEndMin = timeToMinutes(apt.timeTo);
        if (!(slotEndMin <= aptStartMin || slotStartMin >= aptEndMin)) {
          return { blocked: true, reason: 'Private' };
        }
      }
    }
    
    return { blocked: false };
  };

  const generateTimeSlots = () => {
    const slots = [];
    const [startHour, startMin] = settings.startTime.split(':').map(Number);
    const [endHour, endMin] = settings.endTime.split(':').map(Number);
    
    const startMinutes = startHour * 60 + startMin;
    const endMinutes = endHour * 60 + endMin;
    const slotDuration = settings.slotDuration;
    const breakDuration = settings.breakDuration;
    
    // Parse daily break times if set
    const hasMyBreak = settings.myBreakFrom && settings.myBreakTo;
    const myBreakStartMin = hasMyBreak ? timeToMinutes(settings.myBreakFrom) : null;
    const myBreakEndMin = hasMyBreak ? timeToMinutes(settings.myBreakTo) : null;
    
    let currentMinutes = startMinutes;
    let breakInserted = false;
    
    while (currentMinutes + slotDuration <= endMinutes) {
      // Check if we need to insert the daily break before this slot
      if (hasMyBreak && !breakInserted && currentMinutes + slotDuration > myBreakStartMin) {
        // If current slot would overlap with break, stop before break
        if (currentMinutes < myBreakStartMin) {
          // Add slot before break (if there's room)
          const slotStart = `${Math.floor(currentMinutes / 60).toString().padStart(2, '0')}:${(currentMinutes % 60).toString().padStart(2, '0')}`;
          const slotEnd = `${Math.floor(myBreakStartMin / 60).toString().padStart(2, '0')}:${(myBreakStartMin % 60).toString().padStart(2, '0')}`;
          
          // Only add if this creates a valid slot duration
          if (myBreakStartMin - currentMinutes >= slotDuration) {
            slots.push({ start: slotStart, end: slotEnd, type: 'slot' });
          }
        }
        
        // Insert break slot
        const breakStart = `${Math.floor(myBreakStartMin / 60).toString().padStart(2, '0')}:${(myBreakStartMin % 60).toString().padStart(2, '0')}`;
        const breakEnd = `${Math.floor(myBreakEndMin / 60).toString().padStart(2, '0')}:${(myBreakEndMin % 60).toString().padStart(2, '0')}`;
        slots.push({ start: breakStart, end: breakEnd, type: 'break' });
        
        // Resume after break
        currentMinutes = myBreakEndMin;
        breakInserted = true;
        continue;
      }
      
      // Generate regular slot
      const slotStart = `${Math.floor(currentMinutes / 60).toString().padStart(2, '0')}:${(currentMinutes % 60).toString().padStart(2, '0')}`;
      const slotEndMinutes = currentMinutes + slotDuration;
      const slotEnd = `${Math.floor(slotEndMinutes / 60).toString().padStart(2, '0')}:${(slotEndMinutes % 60).toString().padStart(2, '0')}`;
      
      slots.push({ start: slotStart, end: slotEnd, type: 'slot' });
      currentMinutes = slotEndMinutes + breakDuration;
    }
    
    return slots;
  };

  const formatTime12hr = (time24) => {
    const [hour, min] = time24.split(':').map(Number);
    const period = hour >= 12 ? 'PM' : 'AM';
    const hour12 = hour % 12 || 12;
    return `${hour12}:${min.toString().padStart(2, '0')} ${period}`;
  };

  const weekDates = getWeekDates(currentWeekStart);
  const timeSlots = generateTimeSlots();

  return (
    <>
      {/* Alert Messages */}
      {saveMessage && (
        <div className={`save-message ${saveMessage.type}`}>
          {saveMessage.message}
        </div>
      )}

      {/* Template List Modal */}
      {isServiceProvider && showTemplateList && (
        <div className="template-modal-overlay" onClick={() => setShowTemplateList(false)}>
          <div className="template-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Saved Templates</h3>
              <button onClick={() => setShowTemplateList(false)} className="modal-close">×</button>
            </div>
            <div className="template-list">
              {savedTemplates.length === 0 ? (
                <p className="empty-templates">No saved templates yet</p>
              ) : (
                savedTemplates.map(template => (
                  <div key={template.templateId} className={`template-item ${template.isActive ? 'template-active' : 'template-inactive'}`}>
                    <div className="template-info">
                      <div className="template-header">
                        <h4>{template.templateName}</h4>
                        {template.isActive && (
                          <span className="badge-active">ACTIVE</span>
                        )}
                        {!template.isActive && (
                          <span className="badge-inactive">INACTIVE</span>
                        )}
                      </div>
                      <p className="template-date">Updated: {new Date(template.updatedAt).toLocaleDateString()}</p>
                    </div>
                    <div className="template-actions">
                      <button onClick={() => loadTemplate(template)} className="btn-load">
                        <Download size={16} /> Load
                      </button>
                      <button onClick={() => deleteTemplate(template.templateId, template.templateName)} disabled={template.isActive} className="btn-delete-small" >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}

      {/* Main Layout */}
      <div className="app-layout">
        {/* Settings Panel (Left Side) */}
        {isServiceProvider && (
          <div className="settings-panel">
            <div className="settings-card">
              <div className="settings-header">
                <div className="settings-title-wrapper">
                  <Settings className="settings-icon" size={24} />
                  <h2 className="settings-title">Slot Settings</h2>
                  {currentTemplateId && <span className="badge-editing">Editing</span>}
                  {currentTemplateId && currentTemplateIsActive !== null && (
                    <span className={currentTemplateIsActive ? 'badge-status-active' : 'badge-status-inactive'}>
                      {currentTemplateIsActive ? 'ACTIVE' : 'INACTIVE'}
                    </span>
                  )}
                </div>
                {hasChanges && (
                  <button onClick={saveTemplate} className="btn-save" disabled={isLoading}>
                    <Save size={16} />
                    {isLoading ? 'Saving...' : 'Save'}
                  </button>
                )}
              </div>

              <div className="settings-form">
                <div className="form-group">
                  <label className="form-label">Slot Name</label>
                  <input type="text" value={settings.slotName} onChange={(e) => handleSettingChange('slotName', e.target.value)} className="form-input" />
                </div>

                <div className="form-group">
                  <label className="form-label">Date Range</label>
                  <div className="input-group-2">
                    <input type="date" value={settings.dateFrom} onChange={(e) => handleSettingChange('dateFrom', e.target.value)} className="form-input" />
                    <input type="date" value={settings.dateTo} onChange={(e) => handleSettingChange('dateTo', e.target.value)} className="form-input" />
                  </div>
                </div>

                <div className="form-group">
                  <label className="form-label">Available Days</label>
                  <div className="input-group-2">
                    <select value={settings.dayFrom} onChange={(e) => handleSettingChange('dayFrom', e.target.value)} className="form-select">
                      {DAYS_OF_WEEK.map(day => <option key={day} value={day}>{day}</option>)}
                    </select>
                    <select value={settings.dayTo} onChange={(e) => handleSettingChange('dayTo', e.target.value)} className="form-select">
                      {DAYS_OF_WEEK.map(day => <option key={day} value={day}>{day}</option>)}
                    </select>
                  </div>
                </div>

                <div className="form-group">
                  <label className="form-label">Operating Hours</label>
                  <div className="input-group-2">
                    <div className="time-input-wrapper">
                      <label className="time-label">From</label>
                      <input type="time" value={settings.startTime} onChange={(e) => handleSettingChange('startTime', e.target.value)} className="form-input" />
                    </div>
                    <div className="time-input-wrapper">
                      <label className="time-label">To</label>
                      <input type="time" value={settings.endTime} onChange={(e) => handleSettingChange('endTime', e.target.value)} className="form-input" />
                    </div>
                  </div>
                </div>

                <div className="form-group">
                  <label className="form-label">Slot Duration</label>
                  <select value={settings.slotDuration} onChange={(e) => handleSettingChange('slotDuration', Number(e.target.value))} className="form-select">
                    {SLOT_DURATIONS.map(duration => (
                      <option key={duration.value} value={duration.value}>{duration.value} minutes</option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label className="form-label">Break Between Slots</label>
                  <select value={settings.breakDuration} onChange={(e) => handleSettingChange('breakDuration', Number(e.target.value))} className="form-select">
                    {BREAK_DURATIONS.map(duration => (
                      <option key={duration.value} value={duration.value}>{duration.value === 0 ? 'No break' : `${duration.value} minutes`}</option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label className="form-label">Days in Advance for Booking</label>
                  <input type="number" min="0" max="365" value={settings.daysInAdvance} onChange={(e) => handleSettingChange('daysInAdvance', Number(e.target.value))} className="form-input" />
                </div>

                <div className="form-group">
                  <label className="form-label">My Daily Break</label>
                  <div className="input-group-2">
                    <div className="time-input-wrapper">
                      <label className="time-label">From</label>
                      <input type="time" value={settings.myBreakFrom} onChange={(e) => handleSettingChange('myBreakFrom', e.target.value)} className="form-input" />
                    </div>
                    <div className="time-input-wrapper">
                      <label className="time-label">To</label>
                      <input type="time" value={settings.myBreakTo} onChange={(e) => handleSettingChange('myBreakTo', e.target.value)} className="form-input" />
                    </div>
                  </div>
                </div>

                <div className="form-group">
                  <label className="form-label">Private Appointments</label>
                  <button onClick={addPrivateAppointment} className="btn-add">
                    <UserX size={16} /> Add Private Appointment
                  </button>
                  <div className="appointments-list">
                    {privateAppointments.length === 0 ? (
                      <p className="empty-appointments">No private appointments</p>
                    ) : (
                      privateAppointments.map(apt => (
                        <div key={apt.id} className="appointment-card">
                          <div className="appointment-fields">
                            <div className="appointment-field">
                              <label className="form-label-small">Date</label>
                              <input type="date" value={apt.date} onChange={(e) => updatePrivateAppointment(apt.id, 'date', e.target.value)} className="form-input-small" />
                            </div>
                            <div className="appointment-field">
                              <label className="form-label-small">Time From</label>
                              <input type="time" value={apt.timeFrom} onChange={(e) => updatePrivateAppointment(apt.id, 'timeFrom', e.target.value)} className="form-input-small" />
                            </div>
                            <div className="appointment-field">
                              <label className="form-label-small">Time To</label>
                              <input type="time" value={apt.timeTo} onChange={(e) => updatePrivateAppointment(apt.id, 'timeTo', e.target.value)} className="form-input-small" />
                            </div>
                          </div>
                          <button onClick={() => deletePrivateAppointment(apt.id)} className="btn-delete">
                            <Trash2 size={16} />
                          </button>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </div>

              {hasChanges && (
                <div className="unsaved-changes-alert">
                  <p className="unsaved-changes-text">
                    <div className="pulse-dot" />
                    Unsaved changes
                  </p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Preview Panel (Right Side) */}
        <div className={isServiceProvider ? "preview-panel" : "preview-panel-full"}>
          {/* Action Buttons - MOVED HERE (outside preview-card) */}
          {isServiceProvider && (
            <div className="preview-actions">
              <button onClick={() => setShowTemplateList(!showTemplateList)} className="btn-templates">
                <List size={20} />
                Templates ({savedTemplates.length})
              </button>
              <button 
                onClick={resetToNewTemplate} 
                className="btn-new"
                disabled={savedTemplates.length >= 2}
                title={savedTemplates.length >= 2 ? 'Maximum 2 templates allowed' : 'Create a new template'}
              >
                <Plus size={20} />
                New Template
              </button>
            </div>
          )}

          {/* Preview Card */}
          <div className="preview-card">
            <div className="preview-header">
              <h2 className="preview-title">Weekly Preview</h2>
              <div className="week-navigation">
                <button onClick={() => navigateWeek(-1)} className="nav-button">
                  <ChevronLeft size={24} />
                </button>
                <span className="week-label">
                  {weekDates[0].toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })} - {weekDates[6].toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
                </span>
                <button onClick={() => navigateWeek(1)} className="nav-button">
                  <ChevronRight size={24} />
                </button>
              </div>
            </div>

            <div className="grid-container">
              <div className="grid-wrapper">
                <div className="grid-header">
                  <div className="time-header">Time</div>
                  {weekDates.map((date, idx) => {
                    const isInRange = isDateInRange(date);
                    const isDayActive = isDayEnabled(date);
                    const isBookable = isDateBookable(date);
                    return (
                      <div key={idx} className={`day-header ${isInRange && isDayActive ? 'day-header-active' : 'day-header-inactive'}`}>
                        <div className="day-name">{DAYS_OF_WEEK[date.getDay()].slice(0, 3)}</div>
                        <div className={`day-date ${isInRange && isDayActive ? 'day-date-active' : 'day-date-inactive'}`}>{date.getDate()}</div>
                        {isInRange && isDayActive && (
                          <div className={`day-status ${isBookable ? 'day-status-open' : 'day-status-future'}`}>
                            {isBookable ? '✓ Open' : 'Future'}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>

                <div className="slots-container">
                  {timeSlots.map((slot, slotIdx) => (
                    <div key={slotIdx} className={`slot-row ${slot.type === 'break' ? 'break-row' : ''}`}>
                      <div className="time-cell">
                        <Coffee size={14} className={slot.type === 'break' ? 'break-icon' : 'time-icon'} />
                        <div className="time-range">
                          <div className="time-start">{formatTime12hr(slot.start)}</div>
                          <div className="time-end">{formatTime12hr(slot.end)}</div>
                        </div>
                      </div>
                      {slot.type === 'break' ? (
                        // Break row - spans all days
                        <div className="break-cell-full" style={{ gridColumn: '2 / -1' }}>
                          <div className="break-label">
                            <Coffee size={16} />
                            <span>BREAK TIME</span>
                          </div>
                        </div>
                      ) : (
                        // Regular slot - show status for each day
                        weekDates.map((date, dateIdx) => {
                          const isInRange = isDateInRange(date);
                          const isDayActive = isDayEnabled(date);
                          const isBookable = isDateBookable(date);
                          const isAvailable = isInRange && isDayActive;
                          const blockCheck = isSlotBlocked(date, slot.start, slot.end);
                          return (
                            <div key={dateIdx} className={`slot-cell ${!isAvailable ? 'slot-unavailable' : blockCheck.blocked ? 'slot-blocked' : isBookable ? 'slot-available' : 'slot-locked'}`}>
                              <div className="slot-status">
                                {!isAvailable ? (
                                  <div className="status-unavailable">-</div>
                                ) : blockCheck.blocked ? (
                                  <div className="status-blocked">{blockCheck.reason}</div>
                                ) : isBookable ? (
                                  <div className="status-available">Available</div>
                                ) : (
                                  <div className="status-locked">Locked</div>
                                )}
                              </div>
                            </div>
                          );
                        })
                      )}
                    </div>
                  ))}
                </div>

                {timeSlots.length === 0 && (
                  <div className="empty-state">
                    <Clock size={48} className="empty-icon" />
                    <p className="empty-text">No time slots available</p>
                    <p className="empty-subtext">Adjust duration or operating hours</p>
                  </div>
                )}
              </div>
            </div>

            <div className="legend">
              <div className="legend-item">
                <div className="legend-box legend-box-available"></div>
                <span className="legend-text">Available to book</span>
              </div>
              <div className="legend-item">
                <div className="legend-box legend-box-blocked"></div>
                <span className="legend-text">Blocked (Break/Private)</span>
              </div>
              <div className="legend-item">
                <div className="legend-box legend-box-locked"></div>
                <span className="legend-text">Future (not yet bookable)</span>
              </div>
              <div className="legend-item">
                <div className="legend-box legend-box-unavailable"></div>
                <span className="legend-text">Outside range/unavailable</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}